import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.*

/** One row in the merged chronological timeline: either a round slot or a freeform event. */
private data class PrintTimelineRow(
    val dateFrom: String,
    val dateTo: String?,
    val slot: SeasonRoundSlotDto? = null,
    val event: SeasonEventDto? = null,
)

/** "D.M." without leading zeroes or year -- compact on purpose, this is a print handout, not a
 * record of which season it's for (that's already in the page title). */
private fun shortDate(iso: String): String {
    val parts = iso.split("-")
    if (parts.size != 3) return iso
    val (_, m, d) = parts
    return "${d.trimStart('0').ifEmpty { "0" }}.${m.trimStart('0').ifEmpty { "0" }}."
}

private fun shortDateRange(from: String, to: String?): String =
    if (to.isNullOrBlank() || to == from) shortDate(from) else "${shortDate(from)} - ${shortDate(to)}"

private fun printRoundHeader(slot: SeasonRoundSlotDto, leagues: List<SeasonLeagueDto>): String {
    val base = "Kolo ${slot.roundNumber} (${shortDateRange(slot.dateFrom, slot.dateTo)})"
    if (slot.leagueIds.isEmpty()) return base
    val names = slot.leagueIds.map { lid -> leagues.find { it.id == lid }?.name ?: "?" }
    return "$base — vložené kolo jen pro: ${names.joinToString(", ")}"
}

/** Compact, print-optimized whole-season schedule (spec 11): every round's pairings for every
 * league, plus season events, in one continuous chronological table -- unlike the on-screen
 * Rozlosování (one league at a time, with a "Výsledek" column that's meaningless before matches
 * are played). Reachable from Termínová listina admin via "Tisk rozlosování". */
@Composable
fun RozlosovaniTiskScreen(token: String?, seasonId: String, seasonName: String, onBack: () -> Unit) {
    var slots by remember(seasonId) { mutableStateOf<List<SeasonRoundSlotDto>>(emptyList()) }
    var events by remember(seasonId) { mutableStateOf<List<SeasonEventDto>>(emptyList()) }
    var leagues by remember(seasonId) { mutableStateOf<List<SeasonLeagueDto>>(emptyList()) }
    var matches by remember(seasonId) { mutableStateOf<List<MatchWithLeagueDto>>(emptyList()) }
    var teamNames by remember(seasonId) { mutableStateOf<Map<String, String>>(emptyMap()) }
    var loading by remember(seasonId) { mutableStateOf(true) }
    var error by remember(seasonId) { mutableStateOf<String?>(null) }

    LaunchedEffect(seasonId) {
        loading = true
        try {
            slots = Api.fetchRoundSlots(token, seasonId)
            events = Api.fetchSeasonEvents(token, seasonId)
            leagues = Api.fetchLeagues(token, seasonId)
            matches = Api.fetchMatchesForSeason(token, seasonId)
            teamNames = Api.fetchAllTeamsForSeason(token, seasonId)
                .associate { it.teamId to (it.teamNameOverride ?: it.team?.name ?: it.teamId) }
            error = null
        } catch (e: Throwable) {
            error = "Načtení rozlosování selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val matchesByRound = remember(matches) { matches.groupBy { it.roundNumber } }
    val timeline = remember(slots, events) {
        (slots.map { PrintTimelineRow(it.dateFrom, it.dateTo, slot = it) } +
            events.map { PrintTimelineRow(it.dateFrom, it.dateTo, event = it) })
            .sortedBy { it.dateFrom }
    }

    Div(attrs = { classes("card", "print-schedule") }) {
        Div(attrs = { classes("actions", "no-print") }) {
            Button(attrs = { onClick { onBack() } }) { Text("← Zpět") }
            Button(attrs = { onClick { window.print() } }) { Text("Tisknout") }
        }
        H2 { Text("Gambrinus Bowlingliga — Rozlosování — $seasonName") }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        if (!loading && timeline.isEmpty()) {
            Text("Zatím žádné termíny.")
        } else {
            Table(attrs = { classes("print-table") }) {
                timeline.forEach { row ->
                    val ev = row.event
                    val slot = row.slot
                    if (ev != null) {
                        Tr(attrs = { classes("print-event-row") }) {
                            Td { Text("${ev.title} — ${shortDateRange(ev.dateFrom, ev.dateTo)}") }
                        }
                    } else if (slot != null) {
                        key(slot.id) {
                            val roundMatches = matchesByRound[slot.roundNumber] ?: emptyList()
                            val byLeague = roundMatches.groupBy { it.seasonLeague?.name ?: it.seasonLeagueId }
                            Tr(attrs = { classes("print-round-header-row") }) {
                                Td { Text(printRoundHeader(slot, leagues)) }
                            }
                            if (byLeague.isEmpty()) {
                                Tr { Td { Text("Rozpis zatím nevygenerován.") } }
                            } else {
                                // Preserve the season's league order, not the arbitrary grouping order.
                                leagues.mapNotNull { l -> byLeague[l.name]?.let { l.name to it } }.forEach { (leagueName, pairs) ->
                                    Tr(attrs = { classes("print-league-row") }) {
                                        Td {
                                            Span(attrs = { classes("print-league-label") }) { Text("$leagueName:") }
                                            pairs.forEach { m ->
                                                Span(attrs = { classes("print-pair") }) {
                                                    Span(attrs = { classes("print-team") }) { Text(teamNames[m.homeTeamId] ?: m.homeTeamId) }
                                                    Span(attrs = { classes("print-team") }) { Text(teamNames[m.awayTeamId] ?: m.awayTeamId) }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
